// ── Change Order Halt Card ──
// Read-only UI lock that replaces the installer execution surface when an
// active Change Order (Draft / Pending Review) exists for the job.
//
// Allowed actions only:
//   • Report friction / issue
//   • View material details (link to job)
//
// No checklist, no photo capture, no submit, no override, no continue.

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ReportFrictionButton } from "@/components/ReportFrictionButton";
import { Pause, FileText } from "lucide-react";
import { MaterialDetailsSheet } from "@/components/contractor/MaterialDetailsSheet";
import type { ChangeOrderHaltVerdict } from "@/data/changeOrderHalt";

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ChangeOrderHaltCard({
  verdict,
  jobId,
  moduleName = "Execution Surface",
}: {
  verdict: ChangeOrderHaltVerdict;
  jobId: string;
  moduleName?: string;
}) {
  const co = verdict.blockingOrder;
  if (!co) return null;

  return (
    <Card className="border-2 border-warning/50 bg-warning/[0.05]">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-2.5">
          <Pause className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-warning mb-0.5">
              Do not begin install
            </p>
            <h2 className="text-base font-bold leading-tight">
              Work Paused — Change Order in Progress
            </h2>
            <p className="text-[12px] text-foreground/80 leading-snug mt-1">
              A change to this job is being reviewed. Wait for PM confirmation
              before starting or continuing work.
            </p>
          </div>
        </div>

        {/* CO summary block */}
        <div className="rounded-lg border border-border bg-background p-3 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-[11px] font-semibold tabular-nums">
                CO-{co.orderNumber}
              </span>
              <Badge variant="outline" className="text-[10px]">
                {co.status}
              </Badge>
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {formatWhen(co.createdAt)}
            </span>
          </div>
          <p className="text-[12px] text-foreground leading-snug">
            {verdict.reason}
          </p>
          <p className="text-[10px] text-muted-foreground">
            Created by{" "}
            <span className="font-medium text-foreground/80">
              {co.createdBy || "—"}
            </span>
          </p>
        </div>

        {/* Allowed actions only */}
        <div className="flex flex-wrap gap-2 pt-1">
          <MaterialDetailsSheet jobId={jobId} />
          <ReportFrictionButton moduleName={moduleName} />
        </div>
        <p className="text-[10px] text-muted-foreground">
          Contact your PM before proceeding. No override is available from this
          surface.
        </p>
      </CardContent>
    </Card>
  );
}
